import React, { FC } from 'react';
import { Button } from '@/components/ui';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import { useRouter } from '@/navigation';
import useLocale from '@/hooks/useLocale';
import { TypeSubscriptionRecord } from '@/lib/api/subscriptionManager/types';
import { TypeModalFields } from '@/core/generated-types';
import { UniversalCta } from '../UniversalCta';
import SubscriberShopModalWrapper from './SubscriberShopModalWrapper';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations/types/TypeSubscriberShopPageTranslations';

interface ReactivateSubscriptionModalProps extends TypeModalFields {
  isOpen: boolean;
  handleClose: () => void;
  targetedSubscriptionForReactivation?: TypeSubscriptionRecord | undefined;
  translations: TypeSubscriberShopPageTranslations;
}

interface CancelledSubscriptionCtaProps {
  targetedSubscriptionForReactivation: TypeSubscriptionRecord;
  ctaText: string;
}

const CancelledSubscriptionCta: FC<CancelledSubscriptionCtaProps> = ({
  targetedSubscriptionForReactivation,
  ctaText,
}) => {
  const locale = useLocale();
  const { push } = useRouter();
  const { recipient_uuid: userUuid, uuid: subscriptionUuid } =
    targetedSubscriptionForReactivation;
  const reactivateSubscriptionLink =
    targetedSubscriptionForReactivation &&
    constructLegacyCustomerPortalLink(locale, [
      userUuid,
      'account-settings',
      'reactivate',
      subscriptionUuid,
    ]);

  return (
    <Button
      ctaText={ctaText}
      style={BUTTON_STYLES.primary}
      classes="mb-4 h-auto md:w-[300px] w-full"
      onClick={() => push(reactivateSubscriptionLink)}
    />
  );
};

const ReactivateSubscriptionModal: FC<ReactivateSubscriptionModalProps> = ({
  isOpen,
  handleClose,
  targetedSubscriptionForReactivation,
  translations,
  ...props
}) => {
  const { primaryCta, secondaryCta } = props;
  const { reactivateSubscriptionCta } = translations;

  const primaryButton = targetedSubscriptionForReactivation ? (
    <CancelledSubscriptionCta
      targetedSubscriptionForReactivation={targetedSubscriptionForReactivation}
      ctaText={reactivateSubscriptionCta}
    />
  ) : primaryCta ? (
    <UniversalCta
      link={primaryCta}
      className="mb-4 h-auto w-full md:w-[300px]"
    />
  ) : null;

  const secondaryButton = secondaryCta ? (
    <UniversalCta
      link={secondaryCta}
      defaultButtonStyle="Secondary"
      className="mb-4 h-auto w-full md:w-[300px]"
    />
  ) : null;

  const ctaButtons = [primaryButton, secondaryButton].filter(Boolean);

  return (
    <SubscriberShopModalWrapper
      isOpen={isOpen}
      handleClose={handleClose}
      ctaButtons={ctaButtons}
      isCloseable={false}
      {...props}
    />
  );
};

export default React.memo(ReactivateSubscriptionModal);
